import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import translations from './myinstitution.uha.i18n';
import { useIntl } from 'react-intl';
import Button from '../components/button';
import { StudiestedbrukerDto } from '../api/generated/studiested-generated-api';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import {
  OnStedHoverCard,
  StedType,
} from '../components/hierarchy/onhoverhierarchyinfo';
import * as css from './myorganization.psa.module.scss';

interface PropsUh {
  users: StudiestedbrukerDto[];
  handleDelete: (id: string, name: string) => void;
}

const UserTableUh: React.FC<React.PropsWithChildren<PropsUh>> = ({
  users,
  handleDelete,
}) => {
  const [activeCol, setActiveCol] = useState<number>(-1);
  const [sortedUsers, setSortedUsers] = useState<StudiestedbrukerDto[]>();
  const [search, setSearch] = useState<string[]>(['', '', '']);

  const { formatMessage, locale } = useIntl();
  const tableHeaders = [
    { title: formatMessage({ id: translations.name.id }) },
    { title: formatMessage({ id: translations.email.id }) },
    { title: formatMessage({ id: translations.affiliation.id }) },
    { title: formatMessage({ id: translations.role.id }) },
    { title: formatMessage({ id: translations.action.id }) },
  ];

  const filteredAndSortedUsers = React.useMemo(() => {
    const navnSearch = (
      fornavn: string,
      etternavn: string,
      searchIndex: number,
    ) => {
      return (
        `${fornavn} ${etternavn}`.toLowerCase().indexOf(search[searchIndex]) >
        -1
      );
    };

    return (sortedUsers || users).filter((user) => {
      const filterName =
        search[0].length === 0 || navnSearch(user.fornavn, user.etternavn, 0);
      const filterEmail =
        search[1].length === 0 ||
        (user.epost && user.epost.toLowerCase().indexOf(search[1]) > -1);
      const filterPlace =
        search[2].length === 0 ||
        (user.tilhorighet?.studiested.navn &&
          user.tilhorighet.studiested.navn.toLowerCase().indexOf(search[2]) >
            -1);

      return filterName && filterEmail && filterPlace;
    });
  }, [sortedUsers, users, search]);

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = (
    col: number,
    a: StudiestedbrukerDto,
    b: StudiestedbrukerDto,
  ) => {
    switch (col) {
      case 0:
        return compare(a.etternavn, b.etternavn);
      case 1:
        return compare(a.epost, b.epost);
      case 2:
        return compare(
          a.tilhorighet?.studiested.navn || '',
          b.tilhorighet?.studiested.navn || '',
        );
      case 3:
        return compare(
          a.tilhorighet?.brukerrolle || '',
          b.tilhorighet?.brukerrolle || '',
        );
    }
    return 0;
  };

  const sortUsersByActiveCol = (col: number, ascending: boolean) => {
    setSortedUsers(
      [...users].sort((a, b) => {
        return ascending ? sort(col, b, a) : sort(col, a, b);
      }),
    );
  };

  const getRows = () =>
    filteredAndSortedUsers.map((bruker) => {
      let tabHeader = 0;

      return (
        <TableRow key={bruker.id}>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            <NavLink to={`/myinstitution/studiestedbrukerprofil/${bruker.id}`}>
              {bruker.fornavn} {bruker.etternavn}
            </NavLink>
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            {bruker.epost && (
              <a
                target="_top"
                rel="noopener noreferrer"
                href={'mailto:' + bruker.epost}
              >
                {bruker.epost}
              </a>
            )}
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            {bruker.tilhorighet && (
              <div className={css.placeName}>
                {bruker.tilhorighet.studiested.navn}
                <OnStedHoverCard
                  id={bruker.tilhorighet.studiested.id}
                  navn={bruker.tilhorighet.studiested.navn}
                  sted={StedType.STUDIESTED}
                />
              </div>
            )}
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader++].title}>
            {bruker.tilhorighet?.brukerrolle}
          </TableCellTd>
          <TableCellTd header={tableHeaders[tabHeader].title}>
            {bruker.kanSlette && (
              <Button.Tertiary
                id={'deleteButton_' + bruker.id}
                onClick={() => {
                  handleDelete(
                    bruker.id,
                    bruker.fornavn + ' ' + bruker.etternavn,
                  );
                }}
              >
                {formatMessage({ id: translations.deleteUser.id })}
              </Button.Tertiary>
            )}
          </TableCellTd>
        </TableRow>
      );
    });

  const canSortIndices = [0, 1, 2, 3];
  const canSearchIndices = [0, 1, 2];

  const getHeaders = () =>
    tableHeaders.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        sort={sortUsersByActiveCol}
        canSort={canSortIndices.includes(index)}
        search={(col, value) =>
          setSearch(
            search.map((searchString, i) =>
              i === col ? value.toLowerCase() : searchString,
            ),
          )
        }
        canSearch={canSearchIndices.includes(index)}
      >
        {title}
      </TableCellTh>
    ));

  return (
    <Table>
      <TableHeader>
        <TableRow top>{getHeaders()}</TableRow>
      </TableHeader>
      <TableMain>{getRows()}</TableMain>
    </Table>
  );
};

export default UserTableUh;
